import React, { useEffect, useRef, useState } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Btn, CustomDropdown, CustomModal, Loading } from '../../../../components'
import { tableCellCentered } from '../../../../assets/js/styles'
import {
  getCiotsDisponiveis,
  postConsultarCiot,
  postGerarCiot,
} from '../../../../services/api/https/deliveries/ciot.http'
import ModalDeclararCiot from './ModalDeclararCiot'
import {
  BadgeStatusCiot,
  estiloAvisoCiot,
  estiloDicaCiot,
  formatarDataHora,
  formatarDocumento,
  formatarPlaca,
  rotuloCiot,
} from './ciot.util'

const ROTULO_ACAO = {
  declarado: 'Vincular',
  pendente: 'Consultar ANTT',
  gerado: 'Declarar',
  recusado: 'Declarar',
}

export default function ModalVincularCiot({
  visible,
  emissor,
  emissores = [],
  contexto,
  numerosJaInformados = [],
  onClose,
  onVinculado,
  onError,
}) {
  const [emissorCiot, setEmissorCiot] = useState(null)
  const [ciots, setCiots] = useState([])
  const [carregando, setCarregando] = useState(false)
  const [processando, setProcessando] = useState(null)
  const [declarando, setDeclarando] = useState(null)
  const declarandoAnterior = useRef(null)
  const ultimaBusca = useRef(0)

  // Só transportadora (com RNTRC) declara CIOT; pode ser outro emissor do TP, contratado para a viagem
  const opcoesEmissores = emissores.filter(item => item.rntrc)

  const carregar = async () => {
    const busca = ++ultimaBusca.current
    if (!emissorCiot) {
      setCiots([])
      return
    }
    setCarregando(true)
    try {
      const lista = await getCiotsDisponiveis({
        tpId: contexto.tpId,
        emissorId: emissorCiot.id,
        fleetId: contexto.fleetId,
        placa: formatarPlaca(contexto.placa),
      })
      if (busca === ultimaBusca.current) setCiots(lista)
    } catch (error) {
      if (busca === ultimaBusca.current) onError(error)
    } finally {
      if (busca === ultimaBusca.current) setCarregando(false)
    }
  }

  useEffect(() => {
    if (!visible) return
    setDeclarando(null)
    setEmissorCiot((emissor && opcoesEmissores.find(item => item.id === emissor.id)) || null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  useEffect(() => {
    if (visible) carregar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, emissorCiot && emissorCiot.id])

  // A declaração fechou sem vincular (voltar ou recusa): o status do CIOT pode ter mudado
  useEffect(() => {
    if (declarandoAnterior.current && !declarando && visible) carregar()
    declarandoAnterior.current = declarando
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declarando])

  const onBtnGerarClicked = async () => {
    if (!emissorCiot) {
      onError('Escolha o emissor do CIOT')
      return
    }
    setProcessando('gerar')
    try {
      const ciot = await postGerarCiot({
        tpId: contexto.tpId,
        emissorId: emissorCiot.id,
        fleetId: contexto.fleetId,
        placa: formatarPlaca(contexto.placa),
      })
      setDeclarando(ciot)
    } catch (error) {
      onError(error)
    } finally {
      setProcessando(null)
    }
  }

  const onCiotSelecionado = async ciot => {
    if (ciot.status === 'declarado') {
      onVinculado(ciot, emissorCiot)
      return
    }
    if (ciot.status !== 'pendente') {
      setDeclarando(ciot)
      return
    }
    setProcessando(ciot.id)
    try {
      const atualizado = await postConsultarCiot(ciot.id)
      if (atualizado.status === 'declarado') onVinculado(atualizado, emissorCiot)
      else if (['gerado', 'recusado'].includes(atualizado.status)) setDeclarando(atualizado)
      else await carregar()
    } catch (error) {
      onError(error)
    } finally {
      setProcessando(null)
    }
  }

  const visiveis = ciots.filter(ciot => !numerosJaInformados.includes(ciot.numero))
  const outroEmissor = Boolean(emissor && emissorCiot && emissor.id !== emissorCiot.id)

  return (
    <>
      <CustomModal
        isVisible={visible}
        header="Vincular CIOT"
        style={{ width: '90vw', maxWidth: 850 }}
        contentStyle={{ maxHeight: '75vh', overflowY: 'auto' }}
        onClose={onClose}
      >
        <div className="p-grid">
          <div className="p-md-8 p-col-12">
            <CustomDropdown
              optionLabel="razao_social"
              options={opcoesEmissores}
              value={emissorCiot}
              label="Emissor do CIOT (contratado):*"
              filter={true}
              onChange={value => setEmissorCiot(value)}
            />
          </div>
          <div
            className="p-md-4 p-col-12"
            style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}
          >
            <Btn
              disabled={!emissorCiot}
              isLoading={processando === 'gerar'}
              onClick={onBtnGerarClicked}
            >
              Gerar novo CIOT
            </Btn>
          </div>
          {outroEmissor && (
            <div className="p-col-12" style={{ paddingTop: 0, paddingBottom: 0 }}>
              <div style={estiloAvisoCiot}>
                O CIOT será declarado na ANTT por {emissorCiot.razao_social}, com o certificado
                dele. O MDFe de {emissor.razao_social} leva o CNPJ{' '}
                {formatarDocumento(emissorCiot.cnpj)} como responsável pelo CIOT.
              </div>
            </div>
          )}
          <div className="p-col-12" style={estiloDicaCiot}>
            {emissorCiot
              ? `CIOTs de ${emissorCiot.razao_social} (${formatarDocumento(
                  emissorCiot.cnpj,
                )}) que ainda não estão vinculados a nenhum MDFe. Os declarados aparecem quando são desta rota ou deste veículo e a viagem ainda não terminou.`
              : 'Escolha a transportadora que vai declarar o CIOT na ANTT: o emissor do MDFe ou outro emissor do TP contratado para a viagem.'}
          </div>
          <div className="p-col-12">
            {carregando ? (
              <div className="text-center" style={{ marginTop: 20, marginBottom: 20 }}>
                <Loading />
              </div>
            ) : (
              <DataTable
                value={visiveis}
                paginator={visiveis.length > 8}
                rows={8}
                emptyMessage={
                  emissorCiot ? 'Nenhum CIOT disponível: gere um novo' : 'Escolha o emissor do CIOT'
                }
              >
                <Column
                  header="Número"
                  body={ciot => rotuloCiot(ciot)}
                  style={tableCellCentered(170)}
                />
                <Column
                  header="Gerado em"
                  body={ciot => formatarDataHora(ciot.created_at)}
                  style={tableCellCentered(140)}
                />
                <Column
                  header="Status"
                  style={tableCellCentered(130)}
                  body={ciot => <BadgeStatusCiot status={ciot.status} />}
                />
                <Column field="placa" header="Placa" style={tableCellCentered(90)} />
                <Column
                  header="Mensagem"
                  body={ciot => (
                    <span
                      style={{
                        fontSize: '0.8rem',
                        color: ciot.status === 'recusado' ? '#721C24' : '#666',
                      }}
                    >
                      {ciot.mensagem}
                    </span>
                  )}
                />
                <Column
                  style={tableCellCentered(140)}
                  body={ciot => (
                    <Btn
                      isLoading={processando === ciot.id}
                      onClick={() => onCiotSelecionado(ciot)}
                    >
                      {ROTULO_ACAO[ciot.status] || 'Abrir'}
                    </Btn>
                  )}
                />
              </DataTable>
            )}
          </div>
        </div>
      </CustomModal>
      <ModalDeclararCiot
        visible={visible && Boolean(declarando)}
        ciot={declarando}
        emissor={emissorCiot}
        contexto={contexto}
        onClose={() => setDeclarando(null)}
        onDeclarado={ciot => onVinculado(ciot, emissorCiot)}
        onError={onError}
      />
    </>
  )
}
